import { Table } from 'antd'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { BASE_URL } from '../../../constants'
import { useStatsSingleProject } from '../../../hooks/useStatsSingleProject'
import { fetchWithLog } from '../../../service/API'
import { dataStore } from '../../../store/dataStore'
import { Site } from '../../../Types'
import { formatDateToDDMMMYYYY, siteColumnStyle } from './CompletedVisits'

export type FutureVisitType = {
  weekNumber: number
  weekStartingAt: string
  /** compositeId is `${appointment.site.id};${appointment.visit.name}` */
  appointmentsFormatted: { [compositeId: string]: number }
}

type FutureVisitTypeFlattened = {
  weekNumber: number
  weekStartingAt: string
  /** compositeId is `${appointment.site.id};${appointment.visit.name}` */
  [compositeId: string]: number | string
}

export default function FutureVisits() {
  const { projectId } = useParams() as { projectId: string }
  const { sites } = useStatsSingleProject(projectId)
  const futureVisits = dataStore((state) => state.futureVisits)
  const setFutureVisits = dataStore((state) => state.setFutureVisits)
  const selectedSiteCustomIds = dataStore((state) => state.statsSelectedSites)
  console.log('sites ting:', { sites, projectId })
  const selectedSiteIds = sites
    .filter((s) => s.projectId === projectId && s.customId && selectedSiteCustomIds?.includes(s.customId))
    .map((s) => s._id)

  const isFetching = useRef(false)

  useEffect(() => {
    if (!futureVisits && !isFetching.current) {
      isFetching.current = true
      const ccStudyIds = sites
        .filter((site) => site.ccStudyId)
        .map((site) => site.ccStudyId)
        .join(',')

      if (ccStudyIds.length > 0) {
        fetchWithLog(`${BASE_URL}/dashboard/stats/${projectId}/future-visits?ccStudyIds=${ccStudyIds}`)
          .then((res) => {
            setFutureVisits(res.data)
          })
          .catch((err) => {
            console.error(err)
            alert('Something went wrong fetching CC data')
          })
          .finally(() => {
            isFetching.current = false
          })
      } else {
        setFutureVisits([])
      }
    }
  }, [futureVisits, projectId, setFutureVisits, sites])
  console.log('futureVisits:', futureVisits)

  if (!selectedSiteCustomIds) return null

  const showedSites = sites.filter((site) => selectedSiteIds.includes(site._id))

  if (!futureVisits) return <LoadingTable showedSites={showedSites} />

  const visitNames: string[] = futureVisits?.reduce((acc, visitWeek) => {
    const visits = Object.keys(visitWeek.appointmentsFormatted).map((compositeId) => compositeId.split(';')[1])

    for (const visitName of visits) {
      if (!acc.includes(visitName)) {
        acc.push(visitName)
      }
    }

    return acc
  }, [] as string[])

  const screeningVisitsSorted = visitNames.filter((name) => name.toLowerCase().includes('screening')).sort()
  const randoVisitsSorted = visitNames.filter((name) => name.toLowerCase().includes('rando')).sort()
  const visitsSorted = screeningVisitsSorted.concat(randoVisitsSorted)

  const dataFlattened: FutureVisitTypeFlattened[] = futureVisits.map((visit) => {
    return {
      weekNumber: visit.weekNumber,
      weekStartingAt: visit.weekStartingAt,
      ...visit.appointmentsFormatted,
    }
  })

  const columns = defineColumns(showedSites, visitsSorted, futureVisits)

  return (
    <div>
      <Table<FutureVisitTypeFlattened>
        columns={columns}
        dataSource={dataFlattened}
        rowKey="weekStartDate"
        bordered
        size="small"
        pagination={{ pageSize: 2000, hideOnSinglePage: true }}
        scroll={{ y: 450 }}
      />
    </div>
  )
}

// Define columns outside the component
function defineColumns(showedSites: Site[], visitNames: string[], futureVisits: FutureVisitType[]) {
  const total: { [compositeId: string]: number } = {}

  for (const visit of futureVisits) {
    for (const compositeId of Object.keys(visit.appointmentsFormatted)) {
      total[compositeId] = (total[compositeId] ?? 0) + visit.appointmentsFormatted[compositeId]
    }
  }

  return [
    {
      title: 'Week start',
      dataIndex: 'weekStartingAt',
      key: 'weekStartingAt',
      width: 120,
      render: (text, record, index) => {
        const isFirstRow = index === 0
        const style = isFirstRow ? { fontWeight: 'bold', lineHeight: '10px' } : {}

        return (
          <p style={{ textAlign: 'right', ...style }}>
            {formatDateToDDMMMYYYY(text)}
            {isFirstRow && (
              <span className="text-muted" style={{ fontSize: 10 }}>
                <br />
                Incl. completed
              </span>
            )}
          </p>
        )
      },
    },
    {
      title: 'Week',
      dataIndex: 'weekNumber',
      key: 'weekNumber',
      width: 80,
      render: (text, record, index) => {
        const isFirstRow = index === 0
        const style = isFirstRow ? { fontWeight: 'bold', lineHeight: '10px' } : {}
        return (
          <p style={{ ...style }}>
            {text}
            {isFirstRow && (
              <span className="text-muted" style={{ fontSize: 10 }}>
                <br />
                &nbsp;
              </span>
            )}
          </p>
        )
      },
    },
    ...showedSites.map((site) => {
      const siteName = site ? site.officialName : 'Unknown site'

      return {
        title: `${siteName}`,
        key: site._id, // Ensure the key matches the siteId for filtering
        onHeaderCell: () => ({ style: { ...siteColumnStyle, fontWeight: 'bold', color: '#164369' } }),
        children: visitNames.map((visitName, idx) => {
          const compositeId = `${site.ccStudyId};${visitName}`
          const style = idx === 0 ? siteColumnStyle : {}
          const count = total[compositeId] ?? 0

          return {
            title: (
              <span>
                {visitName}
                <br />
                <small className="text-muted">Total: {count}</small>
              </span>
            ),
            dataIndex: compositeId,
            key: compositeId,
            onHeaderCell: () => ({ style: { ...style, fontSize: 12 } }),
            onCell: () => ({ style: style }),
            render: (text, record, index) => {
              const row: FutureVisitTypeFlattened = record
              const val = row[compositeId]
              return val ?? ''
            },
          }
        }),
      }
    }),
  ]
}

function LoadingTable({ showedSites }: { showedSites: Site[] }) {
  const columns = defineLoadingColumns(showedSites)

  return (
    <div>
      <Table<FutureVisitTypeFlattened>
        columns={columns}
        rowKey="weekStartDate"
        bordered
        size="small"
        pagination={{ pageSize: 2000, hideOnSinglePage: true }}
        scroll={{ y: 450 }}
        loading
      />
    </div>
  )
}

function defineLoadingColumns(showedSites: Site[]) {
  return [
    {
      title: 'Week start',
      dataIndex: 'weekStartingAt',
      key: 'weekStartingAt',
      width: 120,
      render: (text: string) => formatDateToDDMMMYYYY(text),
    },
    {
      title: 'Week',
      dataIndex: 'weekNumber',
      key: 'weekNumber',
      width: 80,
    },
    ...showedSites.map((site) => {
      const siteName = site ? site.officialName : 'Unknown site'

      return {
        title: `${siteName}`,
        key: site._id, // Ensure the key matches the siteId for filtering
        onHeaderCell: () => ({ style: { ...siteColumnStyle, fontWeight: 'bold', color: '#164369' } }),
      }
    }),
  ]
}
